// Cart utilities using localStorage

// Helper to sync to cookie so member-panel (port 3001) can read it
// domain belirtilmezse tarayıcı current host'u kullanır (localhost) — her iki port erişebilir
const syncToCookie = (cart) => {
  const value = encodeURIComponent(JSON.stringify(cart));
  document.cookie = `carmenGoldCart=${value}; path=/; max-age=2592000; SameSite=Lax`;
};

export const getCart = () => {
  const cartStr = localStorage.getItem('carmenGoldCart');
  let cart = [];
  if (cartStr) {
    cart = JSON.parse(cartStr);
  } else {
    // Try cookie
    const match = document.cookie.match('(^|;) ?carmenGoldCart=([^;]*)(;|$)');
    if (match) cart = JSON.parse(decodeURIComponent(match[2]));
  }
  return cart;
};

export const addToCart = async (product, quantity = 1) => {
  const cart = getCart();
  
  // Eğer selectedSize yoksa ve gram külçe altın ürünüyse, ürün adından gram değerini çıkar
  let productToAdd = { ...product };
  if (!productToAdd.selectedSize) {
    const isGramGoldBar = (
      product.category === 'gram-kulce-altin' ||
      product.categorySlug === 'gram-kulce-altin' ||
      (product.name?.toLowerCase().includes('külçe') && 
       product.name?.toLowerCase().includes('altın'))
    );
    
    if (isGramGoldBar && product.name) {
      // Ürün adından gram değerini çıkar (örn: "Carmen Gold 5 Gr Külçe Altın 999.9" → "5")
      const gramMatch = product.name.match(/(\d+(?:\.\d+)?)\s*(Gr|gr|gram|Gram)/i);
      if (gramMatch) {
        productToAdd.selectedSize = gramMatch[1];
      }
    }
  }
  
  // cartKey: same product + different size = separate cart item
  const cartKey = productToAdd.selectedSize
    ? `${productToAdd.id}_${productToAdd.selectedSize}`
    : productToAdd.id;
  const existingItem = cart.find(item => item.cartKey === cartKey);

  if (existingItem) {
    existingItem.quantity += quantity;
  } else {
    cart.push({ ...productToAdd, cartKey, quantity });
  }

  // Gram külçe altın için optimizasyon uygula
  const isGramGoldBar = (
    productToAdd.category === 'gram-kulce-altin' ||
    productToAdd.categorySlug === 'gram-kulce-altin' ||
    (productToAdd.name?.toLowerCase().includes('külçe') && 
     productToAdd.name?.toLowerCase().includes('altın'))
  );
  
  let finalCart = cart;
  if (isGramGoldBar) {
    finalCart = await optimizeGramSizes(cart);
  }

  localStorage.setItem('carmenGoldCart', JSON.stringify(finalCart));
  syncToCookie(finalCart);
  return finalCart;
};

export const removeFromCart = (cartKey) => {
  const cart = getCart();
  // Support both old id-based and new cartKey-based removal
  const updatedCart = cart.filter(item => (item.cartKey || item.id) !== cartKey);
  localStorage.setItem('carmenGoldCart', JSON.stringify(updatedCart));
  syncToCookie(updatedCart);
  return updatedCart;
};

export const updateCartItemSize = (cartKey, newSize) => {
  const cart = getCart();
  const index = cart.findIndex(item => (item.cartKey || item.id) === cartKey);
  if (index === -1) return cart;

  const item = cart[index];
  const oldSize = item.selectedSize;
  if (oldSize === newSize) return cart;

  // Yeni cartKey oluştur
  const newCartKey = `${item.id}_${newSize}`;
  
  // Eğer sepette bu yeni ölçüde zaten bir ürün varsa, onları birleştir. (Eski items'da cartKey yoksa dinamik hesapla)
  const existingIndex = cart.findIndex(i => {
    const iKey = i.cartKey || (i.selectedSize ? `${i.id}_${i.selectedSize}` : String(i.id));
    return iKey === newCartKey;
  });
  
  if (existingIndex !== -1 && existingIndex !== index) {
    cart[existingIndex].quantity += item.quantity;
    cart.splice(index, 1);
  } else {
    item.selectedSize = newSize;
    item.cartKey = newCartKey;
  }

  localStorage.setItem('carmenGoldCart', JSON.stringify(cart));
  syncToCookie(cart);
  return cart;
};

// Gram külçe altın ürünlerini saklamak için cache
let gramGoldProductsCache = null;

// API'den gram külçe altın ürünlerini çek ve cache'le
const fetchGramGoldProducts = async () => {
  if (gramGoldProductsCache) return gramGoldProductsCache;
  
  try {
    const { api } = await import('../services/api');
    const response = await api.getProducts({ category: 'gram-kulce-altin', limit: 100 });
    const products = response.data || response;
    
    // Ürünleri gram ve saflığa göre map'e çevir
    const productMap = {};
    products.forEach(product => {
      // Ürün adından gram ve saflık bilgisini çıkar
      const gramMatch = product.name?.match(/(\d+(?:\.\d+)?)\s*(Gr|gr|gram|Gram)/i);
      const purityMatch = product.name?.match(/99[59]\.[09]/);
      
      if (gramMatch && purityMatch) {
        const grams = parseFloat(gramMatch[1]);
        const purity = purityMatch[0];
        const key = `${grams}_${purity}`;
        productMap[key] = {
          id: product.id,
          name: product.name,
          slug: product.slug, // Slug'ı da sakla
          basePrice: product.basePrice,
          grams: grams,
          purity: purity,
          images: product.images, // Resimleri de sakla
          category: product.category,
          categorySlug: product.categorySlug
        };
      }
    });
    
    gramGoldProductsCache = productMap;
    return productMap;
  } catch (error) {
    console.error('[Cache] Failed to load gram gold products:', error);
    return {};
  }
};

// Gram optimizasyonu: Sadece gram külçe altın için otomatik optimizasyon
const optimizeGramSizes = async (cart) => {
  const optimized = [];
  
  // Gram külçe altın ürünlerini yükle
  const gramGoldProducts = await fetchGramGoldProducts();
  
  // Ürünleri kategoriye göre ayır: gram külçe altın vs diğerleri
  const gramGoldItems = [];
  const otherItems = [];
  
  cart.forEach(item => {
    const isGramGoldBar = (
      item.category === 'gram-kulce-altin' ||
      item.categorySlug === 'gram-kulce-altin' ||
      (item.name?.toLowerCase().includes('külçe') && 
       item.name?.toLowerCase().includes('altın'))
    );
    
    if (isGramGoldBar) {
      gramGoldItems.push(item);
    } else {
      otherItems.push(item);
    }
  });
  
  // Diğer ürünleri olduğu gibi ekle
  optimized.push(...otherItems);
  
  // Gram külçe altınları saflığa göre grupla
  const purityGroups = {};
  gramGoldItems.forEach(item => {
    const purityMatch = item.name?.match(/99[59]\.[09]/);
    const purity = purityMatch ? purityMatch[0] : '995.0';
    
    if (!purityGroups[purity]) {
      purityGroups[purity] = [];
    }
    purityGroups[purity].push(item);
  });

  // Her saflık grubu için optimizasyon yap
  for (const [purity, items] of Object.entries(purityGroups)) {
    // Toplam gram hesapla (TÜM ürünlerden)
    let totalGrams = 0;
    items.forEach(item => {
      const size = parseFloat(item.selectedSize) || 0;
      totalGrams += size * item.quantity;
    });

    if (totalGrams === 0) continue;

    // Mevcut ölçüler (büyükten küçüğe)
    const availableSizes = [100, 50, 20, 10, 5, 1];
    
    // Greedy algoritma ile optimal kombinasyonu bul
    let remainingGrams = totalGrams;
    const newItems = [];

    for (const size of availableSizes) {
      const count = Math.floor(remainingGrams / size);
      if (count > 0) {
        // API'den bu gram ve saflıktaki ürünü bul
        const productKey = `${size}_${purity}`;
        const realProduct = gramGoldProducts[productKey];
        
        if (!realProduct) {
          console.warn('[Optimize] Product not found for', size, 'gram', purity);
          continue;
        }
        
        // İlk item'dan template al (resim, kategori vb. için)
        const template = items[0];
        
        newItems.push({
          ...template,
          id: realProduct.id,
          name: realProduct.name,
          slug: realProduct.slug, // Slug'ı ekle
          images: realProduct.images || template.images, // Resimleri güncelle
          category: realProduct.category || template.category,
          categorySlug: realProduct.categorySlug || template.categorySlug,
          selectedSize: String(size),
          cartKey: `${realProduct.id}_${size}`,
          quantity: count,
          basePrice: realProduct.basePrice,
          price: realProduct.basePrice,
          // Ölçü seçiciyi gizlemek için availableSizes'ı kaldır
          availableSizes: null,
          sizes: null,
          sizeLabel: null,
          sizeUnit: null
        });
        remainingGrams -= count * size;
      }
    }

    // Kalan kesirli gram varsa 1 gram ürünü kullan
    if (remainingGrams > 0) {
      const productKey = `1_${purity}`;
      const realProduct = gramGoldProducts[productKey];
      
      if (realProduct) {
        const remainingCount = Math.ceil(remainingGrams);
        
        const template = items[0];
        
        newItems.push({
          ...template,
          id: realProduct.id,
          name: realProduct.name,
          slug: realProduct.slug, // Slug'ı ekle
          images: realProduct.images || template.images,
          category: realProduct.category || template.category,
          categorySlug: realProduct.categorySlug || template.categorySlug,
          selectedSize: '1',
          cartKey: `${realProduct.id}_1`,
          quantity: remainingCount,
          basePrice: realProduct.basePrice,
          price: realProduct.basePrice,
          availableSizes: null,
          sizes: null,
          sizeLabel: null,
          sizeUnit: null
        });
      }
    }

    optimized.push(...newItems);
  }

  return optimized;
};

export const updateCartQuantity = async (cartKey, quantity) => {
  let cart = getCart();
  const item = cart.find(item => (item.cartKey || item.id) === cartKey);

  if (!item) return cart;

  if (quantity <= 0) {
    cart = cart.filter(i => (i.cartKey || i.id) !== cartKey);
  } else {
    item.quantity = quantity;
  }
  
  // Gram optimizasyonu uygula (async)
  const optimizedCart = await optimizeGramSizes(cart);
  
  localStorage.setItem('carmenGoldCart', JSON.stringify(optimizedCart));
  syncToCookie(optimizedCart);
  return optimizedCart;
};

export const updateCartPricesFromLive = (liveProducts) => {
  const cart = getCart();
  let changed = false;
  const newCart = cart.map(item => {
    const liveProduct = liveProducts.find(p => String(p.id) === String(item.id));
    let updatedItem = { ...item };
    let itemChanged = false;

    if (liveProduct) {
      // Fiyat güncelleme
      if (liveProduct.basePrice && String(liveProduct.basePrice) !== String(item.basePrice)) {
        updatedItem.basePrice = liveProduct.basePrice;
        itemChanged = true;
      }
      if (liveProduct.havalePrice && String(liveProduct.havalePrice) !== String(item.havalePrice)) {
        updatedItem.havalePrice = liveProduct.havalePrice;
        itemChanged = true;
      }
      if (liveProduct.creditCardPrice && String(liveProduct.creditCardPrice) !== String(item.creditCardPrice)) {
        updatedItem.creditCardPrice = liveProduct.creditCardPrice;
        itemChanged = true;
      }
      // İndirim alanlarını güncelle
      if (liveProduct.originalPrice !== undefined && liveProduct.originalPrice !== item.originalPrice) {
        updatedItem.originalPrice = liveProduct.originalPrice;
        itemChanged = true;
      }
      if (liveProduct.discountPercentage !== undefined && String(liveProduct.discountPercentage) !== String(item.discountPercentage)) {
        updatedItem.discountPercentage = liveProduct.discountPercentage;
        itemChanged = true;
      }
      if (liveProduct.discountActive !== undefined && liveProduct.discountActive !== item.discountActive) {
        updatedItem.discountActive = liveProduct.discountActive;
        itemChanged = true;
      }
      // Ölçü seçeneklerini güncelleme/ekleme (Eski ürünleri canlandırmak için)
      if (liveProduct.sizes && JSON.stringify(liveProduct.sizes) !== JSON.stringify(item.availableSizes)) {
        updatedItem.availableSizes = liveProduct.sizes;
        updatedItem.sizeLabel = liveProduct.sizes.label;
        updatedItem.sizeUnit = liveProduct.sizes.unit;
        itemChanged = true;
      }
    }

    if (itemChanged) {
      changed = true;
      return updatedItem;
    }
    return item;
  });
  
  if (changed) {
    localStorage.setItem('carmenGoldCart', JSON.stringify(newCart));
    syncToCookie(newCart);
  }
  return newCart;
};

export const clearCart = () => {
  localStorage.removeItem('carmenGoldCart');
  document.cookie = 'carmenGoldCart=; path=/; max-age=0; SameSite=Lax';
  return [];
};

export const getCartTotal = (paymentMethod = 'CREDIT_CARD') => {
  const cart = getCart();
  return cart.reduce((total, item) => {
    const itemPrice = paymentMethod === 'HAVALE'
      ? (item.havalePrice || item.basePrice || item.price || 0)
      : (item.creditCardPrice || (item.basePrice ? Number(item.basePrice) * 1.05 : Number(item.price || 0) * 1.05));
    return total + (parseFloat(itemPrice) * item.quantity);
  }, 0);
};

export const getCartCount = () => {
  const cart = getCart();
  return cart.reduce((count, item) => count + item.quantity, 0);
};
